/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';

import { ImageUploadPreference } from '../../../../ts/preferences';
import { CreateAsyncThunk, PreviewUrl } from '../../../../ts/state';
import * as API from '../../api/preview';
import {
  MIMETYPE_JPEG,
  MIMETYPE_PNG
} from '../../components/preview/ImageUploadModal';
import { ToolingErrorKeys } from '../../util/errors';
import { addMessage, addErrorMessage, ToastStatus } from '../messages/actions';

export const screenshotBackground = createAsyncThunk<
  PreviewUrl,
  { url: string },
  CreateAsyncThunk
>('preview/screenshot', async (params, { dispatch }) => {
  const { url } = params;

  try {
    const response = await API.screenshot(url);
    dispatch(
      addMessage({
        message: 'Your background is successfully loaded.',
        status: ToastStatus.SUCCESS
      })
    );
    const imgType = url.startsWith(ImageUploadPreference.UPLOAD_PNG_PREFIX)
      ? MIMETYPE_PNG
      : MIMETYPE_JPEG;
    const imageAsString = Buffer.from(response).toString('base64');
    return {
      url,
      imageData: `data:${imgType};base64,${imageAsString}`
    };
  } catch (error) {
    if (error.response?.data?.error !== ToolingErrorKeys.UPLOAD_IMAGE_EXPIRED) {
      dispatch(addErrorMessage(error));
    } else {
      // This is specifically a previously uploaded image which has expired and been deleted
      // manipulate the error object to put our message in Redux
      error.message = error.response.data.error;
    }
    throw error;
  }
});

export const imageUpload = createAsyncThunk<
  PreviewUrl,
  { imageId: string; targetFile: File },
  CreateAsyncThunk
>('preview/upload', async (params, { dispatch }) => {
  const { imageId, targetFile } = params;
  try {
    const response = await API.imageUpload(imageId, targetFile);
    dispatch(
      addMessage({
        message: 'Your background is successfully loaded.',
        status: ToastStatus.SUCCESS
      })
    );
    const imgType = imageId.startsWith(ImageUploadPreference.UPLOAD_PNG_PREFIX)
      ? MIMETYPE_PNG
      : MIMETYPE_JPEG;
    const imageAsString = Buffer.from(response).toString('base64');
    return {
      url: imageId,
      imageData: `data:${imgType};base64,${imageAsString}`
    };
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});
